import { countriesData } from "../constant/countries";
import { LOCATION_STORAGE_KEY, isGuestDefaultLocation } from "./locationDefaults";

const normalizeCountryCode = (value) =>
  String(value || "").trim().toUpperCase();

const normalizeText = (value) =>
  String(value || "").trim().toLowerCase();

const countryAliases = {
  AE: ["united arab emirates", "uae", "dubai"],
  IN: ["india", "bharat", "bhopal"],
};

const getSavedLocation = () => {
  if (typeof window === "undefined") return null;

  try {
    const savedLocation = window.localStorage.getItem(LOCATION_STORAGE_KEY);
    return savedLocation ? JSON.parse(savedLocation) : null;
  } catch (error) {
    return null;
  }
};

const getCountryById = (countryId) => {
  if (countryId === null || countryId === undefined || countryId === "") {
    return null;
  }

  return (
    countriesData.find((country) => String(country.id) === String(countryId)) ||
    null
  );
};

const getCountryByCode = (countryCode) => {
  const normalizedCode = normalizeCountryCode(countryCode);

  if (!normalizedCode) return null;

  return (
    countriesData.find(
      (country) => normalizeCountryCode(country.country_code) === normalizedCode
    ) || null
  );
};

const getCountryByText = (countryText) => {
  const normalizedText = normalizeText(countryText);

  if (!normalizedText) return null;

  return (
    countriesData.find((country) => {
      const countryCode = normalizeCountryCode(country.country_code);
      const labels = [
        country.name,
        country.country_code,
        ...(countryAliases[countryCode] || []),
      ].map(normalizeText);

      return labels.some(
        (label) => label && (normalizedText === label || normalizedText.includes(label))
      );
    }) || null
  );
};

const shouldIgnoreStaleDefaultLocation = (location) => {
  const countryCode = normalizeCountryCode(
    location?.countryCode || location?.country_code || location?.detected_country_code
  );

  return (
    isGuestDefaultLocation(location) &&
    countryCode === "AE" &&
    !location?.detected_country_code &&
    location?.location_permission_status !== "granted"
  );
};

export const getDefaultAuthPhoneCountry = ({
  countryId,
  userLocation,
} = {}) => {
  const savedLocation = getSavedLocation();
  const usableUserLocation =
    userLocation && !shouldIgnoreStaleDefaultLocation(userLocation)
      ? userLocation
      : null;
  const usableSavedLocation =
    savedLocation && !shouldIgnoreStaleDefaultLocation(savedLocation)
      ? savedLocation
      : null;
  const locationSources = [usableUserLocation, usableSavedLocation].filter(Boolean);
  const shouldUseCountryId = !userLocation || Boolean(usableUserLocation);

  const countryIdCandidates = [
    usableUserLocation?.country_id,
    usableUserLocation?.countryId,
    usableSavedLocation?.country_id,
    usableSavedLocation?.countryId,
    shouldUseCountryId ? countryId : null,
  ];

  for (const candidate of countryIdCandidates) {
    const matchedCountry = getCountryById(candidate);
    if (matchedCountry) return matchedCountry;
  }

  for (const location of locationSources) {
    const matchedCountry = getCountryByCode(
      location?.countryCode ||
        location?.country_code ||
        location?.detected_country_code
    );

    if (matchedCountry) return matchedCountry;
  }

  for (const location of locationSources) {
    const matchedCountry = getCountryByText(
      location?.country ||
        location?.country_name ||
        location?.city ||
        location?.address ||
        location?.display_address ||
        location?.full_address
    );

    if (matchedCountry) return matchedCountry;
  }

  return getCountryByCode("IN") || countriesData[0];
};

export const isIndiaPhoneCountry = (country) =>
  normalizeCountryCode(country?.country_code) === "IN";
